import { object } from 'prop-types'
import { useTheme } from '@table-library/react-table-library/theme'
import { getTheme } from '@table-library/react-table-library/baseline'
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
} from '@table-library/react-table-library/table'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Buttons from '@ui/elements/Buttons'
import Div from '@ui/layout/Div'
import { useNavigate, useParams } from 'react-router-dom'
import { ROLE_SUPER_ADMIN } from '@router/Routes'
import { useEffect } from 'react'
import { init, updateCurrentRole, updateErrors } from '@store/slice/permissionsSlice'
import { updateNotification } from '@store/slice/mainSlice'
import { get, post } from '@util/xhrAPI'
import SwitcherType from '@ui/inputs/SwitcherType'
import useFormData from "@hook/useFormData"

const UserRoleSubjectAuthorizations = () => {
  const [t] = useTranslation('permissions')
  const theme = useTheme(getTheme())
  const resize = { resizerHighlight: "#dde2eb", resizerWidth: 5 }
  const { attributes, currentRole } = useSelector(state => state.permissions)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const renderFormData = useFormData()
  const { id } = useParams()

  const dataRoleAuthorizations = { nodes: [] }
  if (currentRole) {
    dataRoleAuthorizations.nodes = Object.keys(currentRole.authorizations)
  }

  const renderHeaders = () => {
    return attributes.map(attribute => <HeaderCell resize={resize} key={attribute}>{t(`table.${attribute}`)}</HeaderCell>)
  }

  const renderAttributesPermissions = module => {
    if (currentRole) {
      const moduleAuthorizations = currentRole.authorizations[module]
      return attributes.map(attribute => <Cell resize={resize} key={`permissions-${module}-${attribute}`}>
        <SwitcherType name={`${module}-${attribute}`} value={parseInt(moduleAuthorizations[attribute])} onClick={updateRole} />
      </Cell>)
    }
  }

  const updateRole = e => {
    const name = e.target.name.split('-')
    const value = e.target.value
    const updatedModule = name[0]
    const attribute = name[1]

    dispatch(updateCurrentRole({
      ...currentRole,
      authorizations: {
        ...currentRole.authorizations,
        [updatedModule]: { ...currentRole.authorizations[updatedModule], [attribute]: parseInt(value) }
      }
    }))
  }

  const saveRole = () => {
    const formData = renderFormData(currentRole, ['authorizations'])
    post(`${import.meta.env.VITE_API_URL}/api/user-roles`, formData, response => {
      const { code, errors } = response
      if (200 === code) {
        dispatch(updateNotification({ type: 'is-success', message: t('messages.role_updated') }))
      } else {
        dispatch(updateErrors(errors))
        dispatch(updateNotification({ type: 'is-danger', message: t('messages.error_encountered') }))
      }
    })
  }

  useEffect(() => {
    if (!attributes.length) {
      get(`${import.meta.env.VITE_API_URL}/api/user-roles`, response => {
        const { code, roles, attributes } = response
        if (code === 200) {
          dispatch(init({ roles, attributes }))
        }
      })
    }
  }, [])

  useEffect(() => {
    if ((!currentRole || (currentRole && id !== currentRole.id)) && attributes.length) {
      get(`${import.meta.env.VITE_API_URL}/api/user-roles/${id}`, response => {
        const { code, userRole } = response
        if (code === 200) {
          dispatch(updateCurrentRole(userRole))
        }
      })
    }
  }, [attributes])

  return (
    <Div cssClass='role-show'>
      <Buttons buttons={{
        icon: 'icon-keyboard_arrow_left',
        cssClass: 'is-info is-small back-to-list',
        callback: () => {
          navigate(ROLE_SUPER_ADMIN['managePermissions'])
        },
        text: t('actions.back_to_roles_list')
      }}
      />
      <Table data={dataRoleAuthorizations} theme={theme} layout={{ fixedHeader: true }} className='user-role-permissions'>
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell resize={resize}>{t('table.subject')}</HeaderCell>
                {renderHeaders()}
              </HeaderRow>
            </Header>
            <Body>
              {tableList.map(module =>
                <Row key={module} item={module}>
                  <Cell>{t(`labels.${module}`)}</Cell>
                  {renderAttributesPermissions(module)}
                </Row>
              )}
            </Body>
          </>
        )}
      </Table>
      <Buttons buttons={{
        icon: 'icon-floppy-disk',
        cssClass: 'is-success is-small validate-role',
        callback: saveRole,
        text: t('actions.save_role')
      }}
      />
    </Div>

  )
}

UserRoleSubjectAuthorizations.propTypes = {
  currentRole: object
}

export default UserRoleSubjectAuthorizations
